import { useEffect, useState, useImperativeHandle, useRef, Ref } from "react";
import { PurifyText } from "utils/functions/purifyUtil";
import SelectManyItem from "../questionItems/SelectManyItem";
import SelectManyGroupItem from "../questionItems/SelectManyGroupItem";

import * as c from "./SelectMany.module.css";

type Option = {
  id: number;
  value: string;
  isRequired: boolean;
  groupId: number | null;
  groupName: string;
};

interface SelectManyProps {
  id: number;
  question: string;
  options: Option[];
  onSetValues: (questionId: number, selectedIds: number[]) => void;
  ref?: Ref<{ resetSelection: () => void }>;
}

const SelectMany = ({ id, question, options, onSetValues, ref }: SelectManyProps) => {
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleOptionToggle = (optionId: number) => {
    setSelectedIds((prevSelected) => {
      const isCurrentlySelected = prevSelected.includes(optionId);
      const newSelection = isCurrentlySelected
        ? prevSelected.filter((selectedId) => selectedId !== optionId) // Remove if already selected
        : [...prevSelected, optionId]; // Add if not selected

      // Send the updated array up to the parent component
      onSetValues(id, newSelection);
      return newSelection;
    });
    // Note: We DO NOT close the dropdown here so the user can select multiple items!
  };

  const handleDropdownToggle = (groupName: string) => {
    setOpenDropdown(openDropdown === groupName ? null : groupName);
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
      setOpenDropdown(null);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useImperativeHandle(ref, () => ({
    resetSelection: () => {
      setSelectedIds([]);
      onSetValues(id, []);
    },
  }));

  const nonGroupedOptions = options.filter((option) => option.groupName === "");

  const groupedOptions = options.reduce(
    (acc, option) => {
      if (option.groupName !== "") {
        acc[option.groupName] = acc[option.groupName] || [];
        acc[option.groupName].push(option);
      }
      return acc;
    },
    {} as { [key: string]: Option[] },
  );

  return (
    <div ref={containerRef}>
      <span
        className={c["question-text"]}
        dangerouslySetInnerHTML={{ __html: PurifyText(question) }}
      ></span>

      {nonGroupedOptions.map((option) => (
        <SelectManyItem
          key={option.id}
          value={option.value}
          isSelected={selectedIds.includes(option.id)}
          onToggle={() => handleOptionToggle(option.id)}
          usedInMore={false}
        />
      ))}

      {Object.entries(groupedOptions).map(([groupName, groupOptions]) => (
        <SelectManyGroupItem
          key={groupName}
          groupName={groupName}
          groupOptions={groupOptions}
          selectedIds={selectedIds}
          onToggleOption={handleOptionToggle}
          openDropdown={openDropdown}
          onDropdownToggle={handleDropdownToggle}
        />
      ))}
    </div>
  );
};

export default SelectMany;
